import { useMemo, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, Legend, defaults } from 'chart.js';
import { useAtom } from 'jotai';
import {
  fromUnixTime,
  startOfDay,
  endOfDay,
  format,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  isBefore,
  sub,
  getUnixTime,
} from 'date-fns';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '/src/hooks';
import { formatCurrency } from '/src/utils';
import { goalSelectedDatesAtom } from '/src/features/goals/store';
import { MouseLine } from '/src/chartjs-plugins/MouseLine';
import { goalDistributionQuery } from '/src/features/goals/queries';
import { capitalize } from '@crazyegginc/hatch';
import { getConvertedDateRange } from '/src/utils/date';
import { atomWithDefault } from 'jotai/utils';

import { COLOR_MATRIX } from '/src/features/goals/constants';

const initialView = 'conversions';
defaults.font.family = 'Proxima Nova, Lucida Grande, Tahoma, Verdana, Arial, sans-serif';

ChartJS.register(MouseLine, Legend);

export const goalDistributionDatasetAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  return params.get('view') || initialView;
});

function getDatasetKey(dataset) {
  switch (dataset) {
    case 'conversions':
      return 'conversionsCount';
    case 'visitors':
      return 'uniqueConversionsCount';
    case 'value':
      return 'totalValue';
  }
}

function getYAxisLabel(dataset, currency) {
  if (dataset === 'value') {
    return `Value (${currency})`;
  }
  return capitalize(dataset);
}

export function DistributionChart({ goal, triggerSelection, dataset, isSeparate }) {
  const { currentUser } = useAuthContext();
  const defaultCurrency = currentUser?.settings?.defaultCurrency ?? 'USD';

  const { id } = useParams();

  const [selection] = useAtom(goalSelectedDatesAtom);
  const isSingleOrSeparate = goal.triggers.length === 1 || isSeparate;

  const { data } = useQuery({
    ...goalDistributionQuery({
      id: Number(id),
      presetDate: selection.special,
      startDate: selection.startDate,
      endDate: selection.endDate,
    }),
  });

  const weeklyChart = data?.goalDistribution?.[0]?.granularity === 'WEEK';

  const getDateBlocks = useCallback((start, end, maxDays) => {
    let result = [];
    // Copy start so don't affect original
    let s = new Date(start);
    while (s < end) {
      // Create a new date for the block end that is s + maxDays
      let e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + maxDays);
      // Push into an array. If block end is beyond end date, use a copy of end date
      result.push([new Date(s), e <= end ? e : new Date(end)]);
      // Increment s to the start of the next block which is one day after
      // the current block end
      s.setDate(s.getDate() + maxDays + 1);
    }
    return result;
  }, []);

  const returnDates = useCallback(
    (start, end, weekly) => {
      if (!weekly) {
        return eachDayOfInterval({
          start: fromUnixTime(start),
          end: fromUnixTime(end),
        });
      } else {
        return getDateBlocks(fromUnixTime(start), fromUnixTime(end), 6);
      }
    },
    [getDateBlocks],
  );

  const days = useMemo(() => {
    if (!selection?.startDate && !selection?.endDate && !selection?.special) return [];

    const weekChart = data?.goalDistribution?.[0]?.granularity === 'WEEK';

    if ((!selection?.startDate || !selection?.endDate) && !!selection.special) {
      const { startDate, endDate } = getConvertedDateRange({ special: selection.special }, false);

      return returnDates(startDate, endDate, weekChart);
    }

    return returnDates(selection.startDate, selection.endDate, weekChart);
  }, [selection.startDate, selection.endDate, data, returnDates, selection.special]);

  const chartLabels = useMemo(() => {
    if (!days) return null;
    if (days && weeklyChart) {
      return days.map((interval) => `${format(interval[0], 'MMM dd, yyyy')} - ${format(interval[1], 'MMM dd, yyyy')}`);
    }
    return days.map((day) => format(day, 'MMM dd, yyyy'));
  }, [days, weeklyChart]);

  const simpleLabels = useMemo(() => {
    if (!days) return null;
    if (days && weeklyChart) {
      return days.map((interval) => `${format(interval[0], 'MMM dd')} - ${format(interval[1], 'MMM dd')}`);
    }
    return days.map((day) => format(day, 'MMM dd'));
  }, [days, weeklyChart]);

  const activeDatasetFormatter = useCallback(
    (value) => {
      if (dataset === 'conversion_rate') {
        return `${parseFloat(value).toFixed(1)}%`;
      }
      if (dataset === 'value') {
        return formatCurrency(Number(value), defaultCurrency);
      }

      return value;
    },
    [dataset, defaultCurrency],
  );

  const getStardAndEndTime = useCallback(
    (dataInput, daysInput) => {
      if (!weeklyChart) return null;
      const reduceWrapper = [];
      dataInput.map((list) => {
        return daysInput.map((date, index) => {
          return list.map((item) => {
            if (
              isWithinInterval(startOfDay(fromUnixTime(item.date)), {
                start: startOfDay(index === 0 ? sub(date[0], { weeks: 1 }) : date[0]),
                end: endOfDay(date[1]),
              })
            ) {
              reduceWrapper.push({
                ...item,
                startDate: startOfDay(index === 0 ? sub(date[0], { weeks: 1 }) : date[0]),
                endDate: endOfDay(date[1]),
              });
            } else {
              return null;
            }
          });
        });
      });

      return reduceWrapper.reduce((acc, current) => {
        const matchItem = acc.find(
          (item) =>
            item.id === current.id &&
            getUnixTime(item.startDate) === getUnixTime(current.startDate) &&
            getUnixTime(item.endDate) === getUnixTime(current.endDate),
        );

        if (matchItem) {
          matchItem.conversionsCount += current.conversionsCount;
          matchItem.date = getUnixTime(new Date(current.endDate - (current.endDate - current.startDate) / 2));
          matchItem.endDate = current.endDate;
          matchItem.id = current.id;
          matchItem.sessionsCount += current.sessionsCount;
          matchItem.startDate = current.startDate;
          matchItem.totalValue += current.totalValue;
          matchItem.uniqueConversionsCount += current.uniqueConversionsCount;
          matchItem.uniqueSessionsCount += current.uniqueSessionsCount;
        } else {
          return [...acc, current];
        }

        return acc;
      }, []);
    },
    [weeklyChart],
  );

  const chartValues = useMemo(() => {
    const datasetMapping = getDatasetKey(dataset);
    const distData = data?.goalDistribution ?? [];

    if (!days || !goal || !triggerSelection) return null;
    const dataStartDate = startOfDay(fromUnixTime(goal.createdAt));
    const getTriggersId = triggerSelection.map((x) => x.id);
    const groupDistData = getTriggersId.map((t) => {
      let wrapper = [];
      distData.map((f) => {
        if (f.triggers.length > 0) {
          f.triggers.filter((m) => {
            if (m.id === t) {
              wrapper.push({ ...m, totalValue: f.totalValue });
            }
          });
        }
      });
      return wrapper;
    });
    const groupDistFiltered = getTriggersId.map((t) => {
      if (!weeklyChart) return null;
      const getStartEnd = getStardAndEndTime(groupDistData, days);
      let wrapper = [];
      getStartEnd.filter((f) => {
        if (f.id === t) {
          wrapper.push(f);
        }
      });
      return wrapper;
    });

    const distDataBarFiltered = weeklyChart ? groupDistFiltered : groupDistData;

    const renderDistDataBar = distDataBarFiltered.map((bars, index) => {
      const getData = days.map((date, indexDate) => {
        const day = bars.find((item) => {
          if (Array.isArray(date)) {
            return isWithinInterval(startOfDay(fromUnixTime(item.date)), {
              start: indexDate === 0 ? sub(date[0], { weeks: 1 }) : date[0],
              end: endOfDay(date[1]),
            });
          } else {
            return isSameDay(fromUnixTime(item.date), date);
          }
        });

        const dataPoint = day?.[datasetMapping] ?? (isBefore(date, dataStartDate) ? null : 0);

        if (datasetMapping === getDatasetKey('value')) {
          return dataPoint / 10000;
        }

        return dataPoint;
      });

      const isFirstBar = index === 0 && groupDistData.length > 1;
      const isLastBar = index === groupDistData.length - 1 && groupDistData.length > 1;
      const isInbetweenBar = index > 0 && index < groupDistData.length - 1;

      let barTemplate = {
        label: `${capitalize(dataset)} ${index}`,
        categoryPercentage: 0.5,
        data: getData,
        fill: true,
        backgroundColor: COLOR_MATRIX[index].hex,
        borderRadius: {
          bottomLeft: (isSingleOrSeparate || !isLastBar) && (!isInbetweenBar || isSeparate) ? 3 : 0,
          bottomRight: (isSingleOrSeparate || !isLastBar) && (!isInbetweenBar || isSeparate) ? 3 : 0,
          topLeft: (isSingleOrSeparate || !isFirstBar) && (!isInbetweenBar || isSeparate) ? 3 : 0,
          topRight: (isSingleOrSeparate || !isFirstBar) && (!isInbetweenBar || isSeparate) ? 3 : 0,
        },
        borderColor: ['rgb(255, 255, 255)'],
        borderWidth: {
          left: 0,
          top: isInbetweenBar && !isSeparate ? 1 : 0,
          bottom: isInbetweenBar && !isSeparate ? 1 : 0,
          right: 0,
        },
        hoverBorderWidth: isSeparate ? 2 : isInbetweenBar ? undefined : 0,
        borderSkipped: false,
        stack: `Stack ${isSeparate ? index : 0}`,
        xAxisID: 'xAxisBar',
      };

      return barTemplate;
    });

    const allDatas = renderDistDataBar.map((item) => item.data);

    const reduceAllDatas = allDatas.reduce(
      (acc, array) => acc.map((sum, i) => sum + array[i] + 0.25),
      new Array(allDatas[0]?.length ?? 1).fill(0),
    );
    const transparentData = {
      label: '',
      data: reduceAllDatas,
      categoryPercentage: 0.55,
      backgroundColor: `transparent`,
      hoverBorderRadius: 6,
      borderColor: 'rgb(225, 230, 239)',
      hoverBorderWidth: 6,
      xAxisID: 'xAxisHidden',
    };

    const addTransparent = [...renderDistDataBar, transparentData];
    return isSingleOrSeparate || groupDistData.length < 2 ? renderDistDataBar : addTransparent;
  }, [days, goal, data, dataset, triggerSelection, isSingleOrSeparate, isSeparate, getStardAndEndTime, weeklyChart]);

  const distributionData = useMemo(
    () => ({
      labels: chartLabels,
      datasets: chartValues,
    }),
    [chartLabels, chartValues],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: {
            color: 'rgb(104, 118, 141)',
            padding: 0,
            callback: function (value) {
              return simpleLabels[value];
            },
            autoSkip: false,
            minRotation: weeklyChart ? 15 : undefined,
            maxRotation: weeklyChart ? 82 : undefined,
          },
          grid: {
            display: false,
          },
        },
        xAxisHidden: {
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
        },
        xAxisBar: {
          stacked: true,
          ticks: {
            display: false,
          },
          border: {
            display: false,
          },
          grid: {
            display: false,
          },
        },
        y: {
          stacked: true,
          suggestedMin: 0,
          grace: '5%',
          ticks: {
            padding: 10,
          },
          border: {
            display: false,
          },
          grid: {
            drawTicks: false,
          },
        },
      },
      interaction: {
        mode: 'index',
        axis: 'y',
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (ctx) =>
            externalTooltipHandler(ctx, activeDatasetFormatter, chartValues.length, isSingleOrSeparate),
        },
        legend: {
          display: false,
        },
        mouseline: {
          enabled: false,
        },
      },
    }),
    [activeDatasetFormatter, chartValues.length, isSingleOrSeparate, simpleLabels, weeklyChart],
  );

  if (!goal) return null;

  return (
    <div className="w-full flex flex-row">
      <div className="flex h-[300px] w-5 items-center justify-center">
        <span className="text-caption text-2xs -rotate-90 transform whitespace-nowrap">
          {getYAxisLabel(dataset, defaultCurrency)}
        </span>
      </div>
      <div className="relative flex h-[300px] min-w-[500px] flex-1">
        <Bar data={distributionData} options={options} />
      </div>
    </div>
  );
}

const getOrCreateTooltip = (chart) => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.color = 'white';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.transition = 'all .1s ease';

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const externalTooltipHandler = (context, formatter = null, length, isSingleOrSeparate) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    let sum = 0;
    const bodyLines = tooltip.body.map((b) => b.lines);

    const tableHead = document.createElement('thead');

    const tableBody = document.createElement('tbody');
    tableBody.style.borderTop = '1px solid rgba(255,255,255,0.2)';
    tableBody.style.paddingTop = '5px';

    bodyLines.forEach((body, i) => {
      if (i === bodyLines.length - 1 && !isSingleOrSeparate) {
        return;
      } else {
        const color = COLOR_MATRIX[i].hex;

        const span = document.createElement('span');
        span.style.background = color;
        span.style.borderRadius = '50%';
        span.style.marginRight = '8px';
        span.style.marginLeft = '15px';
        span.style.height = '8px';
        span.style.width = '8px';
        span.style.display = 'inline-block';

        const tr = document.createElement('tr');
        tr.style.backgroundColor = 'inherit';
        tr.style.borderWidth = 0;

        const td1 = document.createElement('td');
        td1.style.borderWidth = 0;
        td1.style.verticalAlign = 'middle';

        const td2 = document.createElement('td');
        td2.style.borderWidth = 0;
        td2.style.paddingLeft = '10px';
        td2.style.fontWeight = 'bold';
        td2.style.paddingRight = '15px';

        const triggerValue = body[0].split(':').map((a) => a.trim())[1];
        sum += parseInt(triggerValue.replace(',', ''));

        const vName = document.createTextNode(`${i + 1}.`);
        const cValue = document.createTextNode(formatter ? formatter(triggerValue) : triggerValue);
        td1.appendChild(span);
        td1.appendChild(vName);
        td2.appendChild(cValue);
        tr.appendChild(td1);
        tr.appendChild(td2);
        tableBody.appendChild(tr);
      }
    });

    if (tooltip.title && tooltip.title.length > 0) {
      const th = document.createElement('th');
      const titleText = document.createTextNode(formatter ? formatter(sum).toLocaleString() : sum.toLocaleString());

      th.appendChild(titleText);
      th.style.textAlign = 'center';
      th.style.paddingBottom = '8px';
      th.style.paddingLeft = '15px';
      th.style.paddingRight = '15px';
      th.style.paddingTop = '8px';
      th.colSpan = length > 1 ? 2 : 1;
      tableHead.appendChild(th);
    }

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    length > 1 ? tableRoot.appendChild(tableBody) : null;
  }

  const { offsetLeft: positionX, offsetTop: positionY, offsetWidth: canvasWidth } = chart.canvas;

  // Calculate the initial tooltip position
  let left = positionX + tooltip.caretX;
  let top = positionY + tooltip.caretY - tooltipEl.offsetHeight - 2;

  // Adjust if the tooltip goes beyond the canvas on the right side
  if (left + tooltipEl.offsetWidth > positionX + canvasWidth) {
    left = positionX + canvasWidth - tooltipEl.offsetWidth - 10;
  }

  // Adjust if the tooltip goes beyond the canvas on the left side
  if (left < positionX) {
    left = positionX + 10; // 10px padding from the edge
  }

  // Adjust if the tooltip goes beyond the canvas on the top
  if (top < positionY) {
    top = positionY + tooltip.caretY + 10; // 10px padding below the cursor if too high
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top - 6}px`;
  tooltipEl.style.font = tooltip.options.bodyFont.string;
  tooltipEl.classList.add('tooltipGoals');
};
